import * as React from 'react';
import moment from 'moment';
import propz from 'propz';
import { EventInvitationMessage } from 'Src/models/message';
import { AppUser } from 'Src/views/App/App';
import { getEventNameForParent } from 'Src/helpers/accessor/accessor';
import { getSchoolEventLocation } from 'Src/helpers/event/schoolEventViewHelper';
import './ParentAvailabilityOverview.scss';

interface Props {
  items: EventInvitationMessage[];
  user: AppUser;
  readOnly?: boolean;
  onReply?: (item: EventInvitationMessage) => void;
  onView?: (item: EventInvitationMessage) => void;
}

const personName = (item: EventInvitationMessage) => {
  const person: any = propz.get(item, ['playerDetailsData'], {});
  return [person.firstName, person.lastName].filter(Boolean).join(' ') || 'Student';
};

const eventId = (item: EventInvitationMessage) => String(propz.get(item, ['eventData', 'id'], item.eventId) || item.id);

const personId = (item: EventInvitationMessage) =>
  String(
    propz.get(item, ['playerDetails', 'permissionId'], '') ||
      propz.get(item, ['playerDetails', 'userId'], '') ||
      propz.get(item, ['playerDetailsData', 'id'], '') ||
      item.id
  );

const revisionType = (item: EventInvitationMessage) => String((item as any).availabilityRevisionType || '').toUpperCase();
const isResponse = (item: EventInvitationMessage) =>
  revisionType(item) === 'RESPONSE' || typeof (item as any).isTakePart === 'boolean';

const latestFirst = (left: EventInvitationMessage, right: EventInvitationMessage) =>
  moment(right.createdAt).valueOf() - moment(left.createdAt).valueOf();

export const ParentAvailabilityOverview: React.FC<Props> = ({ items, user, readOnly, onReply, onView }) => {
  const groups = items.reduce((result, item) => {
    const key = eventId(item);
    (result[key] || (result[key] = [])).push(item);
    return result;
  }, {} as Record<string, EventInvitationMessage[]>);

  if (!items.length) {
    return <div className="paoEmpty"><span aria-hidden="true">✓</span><strong>{readOnly ? 'No availability reports' : 'You’re all caught up'}</strong><p>{readOnly ? 'Reports for this period will appear here.' : 'The school isn’t waiting for any availability replies.'}</p></div>;
  }

  return <div className="paoList">{Object.keys(groups).map(key => {
    const rows = groups[key];
    const first = rows[0];
    const event: any = propz.get(first, ['eventData'], {});
    const start = moment(event.startTime);
    const latestRequestForEvent = rows.filter(item => !isResponse(item)).sort(latestFirst)[0];
    const deadline = moment(
      propz.get(
        latestRequestForEvent || first,
        ['availabilityDeadline'],
        propz.get(latestRequestForEvent || first, ['deadlineForAnswers'], event.startTime)
      )
    );
    const histories = rows.reduce((result, row) => {
      const key = personId(row);
      (result[key] || (result[key] = [])).push(row);
      return result;
    }, {} as Record<string, EventInvitationMessage[]>);
    const people = Object.keys(histories).map(personKey => {
      const history = histories[personKey].slice().sort(latestFirst);
      const responses = history.filter(isResponse);
      const requests = history.filter(item => !isResponse(item));
      return { history, responses, requests, latest: responses[0] || requests[0] };
    });
    const waiting = people.filter(person => !person.responses.length);
    const title = getEventNameForParent(event, rows.map(row => String(propz.get(row, ['playerDetails', 'userId'], ''))).filter(Boolean)) || event.name || event.title || propz.get(event, ['sport', 'name'], 'Event');
    const location = getSchoolEventLocation(event, user);
    const school = propz.get(first, ['schoolData', 'name'], 'the school');
    const latestSchoolRequest = rows
      .filter(item => !isResponse(item) && propz.get(item, ['details'], ''))
      .sort(latestFirst)[0];
    const schoolMessage = propz.get(latestSchoolRequest, ['details'], '');
    return <article className={`paoEvent ${waiting.length && !readOnly ? 'is-waiting' : ''}`} key={key}>
      <header className="paoEvent__head">
        <time className="paoDate" dateTime={start.isValid() ? start.toISOString() : undefined}><small>{start.isValid() ? start.format('ddd') : 'DATE'}</small><b>{start.isValid() ? start.format('D') : '—'}</b><small>{start.isValid() ? start.format('MMM') : 'TBC'}</small></time>
        <div><h3>{title}</h3><p>{start.isValid() ? start.format('ddd D MMM YYYY · HH:mm') : 'Date to be confirmed'}{location ? ` · ${location}` : ''}</p></div>
        {!readOnly && waiting.length && deadline.isValid() ? <div className="paoDeadline"><span>Reply by</span><b>{deadline.format('ddd D MMM, HH:mm')}</b></div> : null}
      </header>
      {schoolMessage ? <div className="paoMessage"><b>Message from {school}</b><span>{schoolMessage}</span></div> : null}
      <div className="paoRows">{people.map(person => {
        const row = person.latest;
        const name = personName(row);
        const response = person.responses[0];
        const latestRequest = person.requests[0];
        const value = response && (response as any).isTakePart;
        const pending = !response;
        const expired = readOnly && pending;
        const status = expired ? 'No reply' : pending ? 'Needs your reply' : value ? 'Available' : 'Not available';
        const sender = propz.get(response, ['sender', 'fullName'], '');
        const senderId = String(propz.get(response, ['sender', 'userId'], ''));
        const playerId = String(propz.get(response, ['playerDetails', 'userId'], ''));
        const senderLabel = senderId && senderId === String((user as any).userId)
          ? 'you'
          : senderId && senderId === playerId
          ? `${personName(response)} (student)`
          : sender || 'a parent';
        const sentAt = moment((response || latestRequest || row).createdAt);
        const requestedAt = latestRequest ? moment(latestRequest.createdAt) : null;
        const reminders = person.requests.filter(item => revisionType(item) === 'REMINDER');
        const changedCount = Math.max(0, person.responses.length - 1);
        const initials = name.split(' ').map(part => part[0]).slice(0, 2).join('').toUpperCase();
        return <div className="paoRow" key={row.id}>
          <span className="paoAvatar">{initials}</span>
          <div className="paoRow__body"><div><strong>{name}</strong><span className={`paoStatus is-${expired ? 'none' : pending ? 'waiting' : value ? 'yes' : 'no'}`}>{status}</span></div><small>{pending ? (expired ? 'The deadline passed without a reply' : `Requested ${requestedAt && requestedAt.isValid() ? requestedAt.format('D MMM, HH:mm') : ''}${reminders.length ? ` · reminder ${moment(reminders[0].createdAt).format('D MMM, HH:mm')}` : ''}`) : `Reported by ${senderLabel}${sentAt.isValid() ? ` · ${sentAt.format('D MMM, HH:mm')}` : ''}${changedCount ? ` · changed ${changedCount}×` : ''}`}</small>{response && propz.get(response, ['details'], '') ? <em>“{propz.get(response, ['details'], '')}”</em> : null}</div>
          <div className="paoActions">{onView && (!pending || readOnly) ? <button type="button" onClick={() => onView(response || row)}>View</button> : null}{!readOnly && onReply ? <button type="button" className={pending ? 'is-primary' : ''} onClick={() => onReply(response || row)}>{pending ? 'Reply' : 'Change'}</button> : null}</div>
        </div>;
      })}</div>
    </article>;
  })}</div>;
};

ParentAvailabilityOverview.displayName = 'ParentAvailabilityOverview';
